import React, { useState, useEffect } from 'react';
import { Card, Button, Pagination } from 'antd';
import { DealsApi } from 'api/api';
import { DealStage, DealListDto } from 'api/models';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import { useTranslation } from 'react-i18next';
import DealKanbanBoard from './components/DealKanbanBoard';
import DealsTable from './components/DealsTable';
import DealsPageFlter from './components/DealsPageFlter';
import { useNavigate } from 'react-router-dom';

const dealsApi = new DealsApi();

const DealsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(
    () => (localStorage.getItem('dealsViewMode') as 'table' | 'kanban') || 'kanban',
  );
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [deals, setDeals] = useState<DealListDto[]>([]);
  const [filters, setFilters] = useState({
    searchTerm: '',
    stage: undefined as DealStage | undefined,
    isActive: true,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [kanbanPageIndex, setKanbanPageIndex] = useState(0);

  useEffect(() => {
    setDeals([]);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDeals(1, pagination.pageSize);
  }, [filters]);

  const fetchDeals = async (page = pagination.current, pageSize = pagination.pageSize, append = false) => {
    try {
      setLoading(true);
      const response = await dealsApi.apiDealsGet({
        ...filters,
        pageNumber: page,
        pageSize: pageSize,
      });

      const newDeals = response.data.items || [];
      setDeals(prev => (append ? [...prev, ...newDeals] : newDeals));
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: response.data.totalCount || 0,
      });
      setHasMore(response.data.hasNextPage || false);
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (dealId: number, newStage: DealStage) => {
    try {
      setLoading(true);
      await dealsApi.apiDealsIdStagePut({
        id: dealId,
        updateDealStageCommand: {
          id: dealId,
          stage: newStage,
        },
      });
      await fetchDeals(); // Refresh the deals list
    } catch (error) {
      // Handle error
      console.error('Failed to update deal stage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dealId: number) => {
    try {
      setLoading(true);
      await dealsApi.apiDealsIdDelete({ id: dealId });
      await fetchDeals(); // Refresh the deals list
    } catch (error) {
      // Handle error
      console.error('Failed to delete deal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchDeals(pagination.current + 1, pagination.pageSize, true);
    }
  };

  const handleViewModeChange = (mode: 'table' | 'kanban') => {
    setViewMode(mode);
    localStorage.setItem('dealsViewMode', mode);
    if (mode === 'table') {
      setTablePageIndex(0);
    } else {
      setKanbanPageIndex(0);
    }
  };

  const onPaginationChange = (pageNumber: number, pageSize: number) => {
    setPagination({ ...pagination, current: pageNumber, pageSize });
  };

  const currentPageIndex = viewMode === 'table' ? tablePageIndex : kanbanPageIndex;
  const setCurrentPageIndex = viewMode === 'table' ? setTablePageIndex : setKanbanPageIndex;

  return (
    <>
      <PageHeader
        ghost
        title={t('deals:title', 'Deals')}
        buttons={[
          <Button
            key="create"
            type="primary"
            onClick={() => navigate('/deals/new')}
            icon={<FeatherIcon icon="plus" size={16} />}
          >
            {t('deals:create', 'New Deal')}
          </Button>,
        ]}
      />

      <Main>
        <Card>
          <DealsPageFlter
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onSearch={searchTerm => setFilters(prev => ({ ...prev, searchTerm }))}
            onStageFilter={stage => setFilters(prev => ({ ...prev, stage }))}
            onProjectFilter={projectId => setFilters(prev => ({ ...prev, projectId }))}
          />

          {viewMode === 'kanban' ? (
            <DealKanbanBoard
              deals={deals}
              loading={loading}
              onStageChange={handleStageChange}
              onDelete={handleDelete}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
            />
          ) : (
            <DealsTable
              deals={deals}
              loading={loading}
              onRefresh={fetchDeals}
              onDelete={handleDelete}
              pagination={{
                current: tablePageIndex + 1,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: page => setTablePageIndex(page - 1),
                showSizeChanger: true,
                pageSizeOptions: [10, 50, 100, 1000],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          )}
        </Card>
      </Main>
    </>
  );
};

export default DealsPage;
